import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { ConsentAnalytics } from './components/ConsentAnalytics';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { AccessibilityPage } from './pages/AccessibilityPage';
import { GraciasPage } from './pages/GraciasPage';
import { GuiaPage } from './pages/GuiaPage';
import { PedidoPage } from './pages/PedidoPage';
import { FotoPage } from './pages/FotoPage';
import { RevisionPage } from './pages/RevisionPage';
import { AppointmentPage } from './pages/AppointmentPage';
import { VideoPage } from './pages/VideoPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// Travelpayouts "Drive" (afiliados de viaje). El script de un tercero se carga SOLO en la
// portada, donde está la franja "Planee su viaje". Nunca debe correr en las páginas donde el
// cliente escribe datos personales (pedido, pago, foto, revisión, gracias). Como el sitio es
// una sola página (SPA), un script ya cargado no se puede descargar; por eso, si el cliente
// pasa de la portada a una página sensible, se recarga la página completa para empezar
// limpia y sin el script.
const TP_SRC = 'https://emrld.ltd/NTg3MDcx.js?t=587071';
const TP_ALLOWED = ['/'];
const TP_SENSITIVE = ['/pedido', '/gracias', '/foto', '/revision', '/appointment'];
let tpLoaded = false;
function TravelpayoutsLoader() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    if (tpLoaded && TP_SENSITIVE.includes(path)) {
      window.location.reload();
      return;
    }
    if (!tpLoaded && TP_ALLOWED.includes(path)) {
      try {
        const s = document.createElement('script');
        s.async = true;
        s.setAttribute('data-cmp-ab', '2');
        s.src = TP_SRC;
        document.head.appendChild(s);
        tpLoaded = true;
      } catch {
        /* sin el script el sitio funciona igual */
      }
    }
  }, [pathname]);
  return null;
}

// PRUEBA (2026-10-18): direcciones propias de Noor que redirigen al enlace de afiliado de
// Travelpayouts. Ejemplo: noorpassportservices.com/viaje/seguro. El navegador del cliente
// termina pasando por el enlace tpo.lu, que es el que registra el clic. Las tarjetas de la
// portada TODAVÍA usan los enlaces tpo.lu directos; se cambian solo después de confirmar en
// Travelpayouts (Reports) que estos clics se registran.
const GO_LINKS: Record<string, string> = {
  vuelos: 'https://kiwi.tpo.lu/aC11WFE8',
  esim: 'https://airalo.tpo.lu/vsFqn4Kv',
  seguro: 'https://ektatraveling.tpo.lu/FTOuQPfI',
  autos: 'https://getrentacar.tpo.lu/hCX2Q4IK'
};
function GoRedirect() {
  const {
    slug
  } = useParams();
  const target = slug ? GO_LINKS[slug.toLowerCase()] : undefined;
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    if (target) window.location.replace(target);
    return () => {
      m.remove();
    };
  }, [target]);
  return <div className="max-w-xl mx-auto px-4 py-20 text-center" data-id="element-0" data-mp="m5ng5cke0rqk">
      {target ? <p className="text-brand-textPrimary" data-id="element-1" data-mp="h6ki4c2trls3">
          Redirecting…{' '}
          <a className="underline" href={target} rel="sponsored noopener noreferrer" data-id="element-2" data-mp="c8j3c3cfm8mf">
            Continue
          </a>
        </p> : <p className="text-brand-textPrimary" data-id="element-3" data-mp="19vhq9mdbo274">
          <a className="underline" href="/" data-id="element-4" data-mp="14x6gtcqxvtpf">
            Noor Passport Services
          </a>
        </p>}
    </div>;
}

// Cada página pública tiene su propio título ("Precios | Noor Passport Services").
// Así Google puede mostrar cada página con un nombre claro debajo del resultado de
// la marca. La portada y las páginas internas muestran solo "Noor Passport Services".
const BRAND = 'Noor Passport Services';
const PAGE_TITLES: Record<string, Record<Language, string>> = {
  '/how-it-works': {
    en: 'How it works',
    es: 'Cómo funciona',
    ht: 'Kijan li mache',
    vi: 'Cách hoạt động'
  },
  '/pricing': {
    en: 'Packages & pricing',
    es: 'Paquetes y precios',
    ht: 'Pakè ak pri',
    vi: 'Gói và bảng giá'
  },
  '/faq': {
    en: 'Frequently asked questions',
    es: 'Preguntas frecuentes',
    ht: 'Kesyon moun poze souvan',
    vi: 'Câu hỏi thường gặp'
  },
  '/contact': {
    en: 'Contact',
    es: 'Contacto',
    ht: 'Kontak',
    vi: 'Liên hệ'
  },
  '/guia': {
    en: 'Which form do I need?',
    es: '¿Qué formulario necesito?',
    ht: 'Ki fòm mwen bezwen?',
    vi: 'Tôi cần mẫu đơn nào?'
  },
  '/privacy-policy': {
    en: 'Privacy policy',
    es: 'Política de privacidad',
    ht: 'Politik vi prive',
    vi: 'Chính sách quyền riêng tư'
  },
  '/terms-of-service': {
    en: 'Terms of service',
    es: 'Términos de servicio',
    ht: 'Kondisyon sevís',
    vi: 'Điều khoản dịch vụ'
  },
  '/refund-policy': {
    en: 'Refund policy',
    es: 'Política de reembolsos',
    ht: 'Politik ranbousman',
    vi: 'Chính sách hoàn tiền'
  },
  '/accessibility': {
    en: 'Accessibility',
    es: 'Accesibilidad',
    ht: 'Aksesibilite',
    vi: 'Khả năng tiếp cận'
  }
};
function PageTitle() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useLanguage();
  useEffect(() => {
    const key = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    const p = PAGE_TITLES[key];
    document.title = p ? `${p[language]} | ${BRAND}` : BRAND;
  }, [pathname, language]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-5" data-mp="33hpkbb21lbfl">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-6" data-mp="384f9bagbdo6h">
      <BrowserRouter data-id="element-7" data-mp="384xgh24ilmt6">
        <ScrollToTop data-id="element-8" data-mp="387tawgh5ltcv" />
        <PageTitle data-id="element-9" data-mp="3d49ztcw6gbr0" />
        <TravelpayoutsLoader data-id="element-10" data-mp="3d3qpah8uwpn0" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-11" data-mp="3d2p43jy4mmrk">
          <SkipLink data-id="element-12" data-mp="2zbu3sw1p1dzl" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-13" data-mp="2ue20l5vlm5g3" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-14" data-mp="2ge9btg3mtzar">
            <Header data-id="element-15" data-mp="2gfkf7et6151c" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-16" data-mp="2ggn79q285diu">
            <Routes data-id="element-17" data-mp="30vnatzcb1abc">
              <Route path="/" element={<Home data-id="element-19" data-mp="30tawdw5r9i31" />} data-id="element-18" data-mp="30u2imgdgq3gn" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-21" data-mp="2vxc3bv9hfbwa" />} data-id="element-20" data-mp="2vwanzelfo38r" />
              <Route path="/pricing" element={<PricingPage data-id="element-23" data-mp="2qwxre24rfhsz" />} data-id="element-22" data-mp="2vu7q68e1h5th" />
              <Route path="/faq" element={<FAQPage data-id="element-25" data-mp="2qvcysi20a8xr" />} data-id="element-24" data-mp="2qw69fu435il6" />
              <Route path="/contact" element={<Contact data-id="element-27" data-mp="2lyw8bh7c3ihi" />} data-id="element-26" data-mp="2ly4o12j6t76j" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-29" data-mp="2hwj5b0jz1yof" />} data-id="element-28" data-mp="2lxl45g9z88pv" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-31" data-mp="2d62nz5d15sws" />} data-id="element-30" data-mp="2hxkqhxupc1jv" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-33" data-mp="286gdh3ol6qro" />} data-id="element-32" data-mp="2d4hy3t1nuy59" />
              <Route path="/accessibility" element={<AccessibilityPage data-id="element-35" data-mp="284lo9d6a8chs" />} data-id="element-34" data-mp="287hqhdp5xnc1" />
              <Route path="/gracias" element={<GraciasPage data-id="element-37" data-mp="2396gn2yvbctt" />} data-id="element-36" data-mp="23851oneqe66t" />
              <Route path="/guia" element={<GuiaPage data-id="element-39" data-mp="1y91w91uu9xyb" />} data-id="element-38" data-mp="235sdvtmal1lu" />
              <Route path="/pedido" element={<PedidoPage data-id="element-41" data-mp="1yamqeqd8revn" />} data-id="element-40" data-mp="1y78r5qzvjlsf" />
              <Route path="/foto" element={<FotoPage data-id="element-43" data-mp="1u7zzcroe9xzi" />} data-id="element-42" data-mp="1u76ujw8zxik0" />
              <Route path="/revision" element={<RevisionPage data-id="element-45" data-mp="9p66g5nekpuv" />} data-id="element-44" data-mp="1u7qdngaduczm" />
              <Route path="/appointment" element={<AppointmentPage data-id="element-47" data-mp="9qz4iwjya1r9" />} data-id="element-46" data-mp="9nuu3hfw69s5" />
              <Route path="/video" element={<VideoPage data-id="element-49" data-mp="dp6kvqai511x" />} data-id="element-48" data-mp="dpygv280h0sq" />
              <Route path="/viaje/:slug" element={<GoRedirect data-id="element-51" data-mp="ipucad2n7jwo" />} data-id="element-50" data-mp="dskof1uwjgay" />
            </Routes>
          </main>
          <Footer data-id="element-52" data-mp="inhxg8s6lpnu" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-53" data-mp="no5lqmp0zsed" />
          {/* Aviso de analítica: Google Analytics solo carga si el visitante acepta */}
          <ConsentAnalytics data-id="element-54" data-mp="3m486kwj28itr" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}